import { useState, useEffect, useRef } from 'react';
import { Toaster, toast } from 'sonner';
import { AnimatePresence, motion } from 'framer-motion';
import './index.css';
import { supabase } from './supabaseClient';
import LoginPage from './components/LoginPage';
import Sidebar from './components/Sidebar';
import NewOrderPage from './components/NewOrderPage';
import HistoryPage from './components/HistoryPage';
import AdminPage from './components/AdminPage';
import ClientsPage from './components/ClientsPage';
import TeamPage from './components/TeamPage';
import ReadinessPage from './components/ReadinessPage';
import LiveDashboard from './components/LiveDashboard';
import SettingsPage from './components/SettingsPage';
import CommandPalette from './components/CommandPalette';
import SetPasswordPage from './components/SetPasswordPage';
import { LogoMark } from './components/ui/Logo';
import { fetchMyCredits } from './lib/credits';
import { getCached, setCached, setCacheScope } from './lib/cache';
import { syncPageToUrl } from './lib/urlState';
import { useOrdersWatch } from './lib/ordersWatch';

const THEME_KEY = 'mams.theme';
// T2.4: linki z maili — ?page=readiness&order=<id> | ?page=history | ?page=clients.
// 21.10: ten sam adres trzyma bieżącą stronę (i wybrany element) — F5 wraca w to samo miejsce.
const URL_PAGES = ['readiness', 'history', 'clients', 'team', 'new', 'live', 'settings', 'myclient', 'admin'];
let DEEP = (() => {
  try {
    const q = new URLSearchParams(window.location.search);
    const page = q.get('page');
    return URL_PAGES.includes(page) ? { page, order: q.get('order') } : null;
  } catch { return null; }
})();

function applyTheme(theme) {
  const root = document.documentElement;
  let effective = theme;
  if (theme === 'system') {
    effective = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  root.classList.toggle('dark', effective === 'dark');
}

export default function App() {
  const [user, setUser]         = useState(null);
  const [session, setSession]   = useState(null);
  const [page, setPage]         = useState(DEEP?.page || 'new');
  const [credits, setCredits]   = useState(null);
  // Plan v9/A2: skąd płacimy — { scope: 'org'|'user', orgName, unlimited }
  const [creditsInfo, setCreditsInfo] = useState(null);
  const [isAdmin, setIsAdmin]   = useState(false);
  // T29: konto klienta — profiles.tenant_id; widzi tylko swojego klienta (Mój klient + Gotowość).
  const [clientTenantId, setClientTenantId] = useState(null);
  // Plan v8/E6: organizacja (agencja / Mediafy) — { id, name, role, credits }; null = brak (albo tabela jeszcze nie istnieje).
  const [org, setOrg] = useState(null);
  // v9/A3: świeżo zarejestrowana agencja → kreator „pierwszy klient”.
  const [onboarding, setOnboarding] = useState(false);
  const signupTried = useRef(false);
  // T2.1: jedno pobranie danych konta naraz. Po logowaniu fetchUserData wołają i handleLogin, i onAuthStateChange —
  // dwa równoległe biegi ścigały się z org-signup i świeża agencja widziała 0 kredytów, bez „Klienci” i bez kreatora
  // (dopiero F5 pokazywał organizację). Drugi bieg czeka teraz na pierwszy.
  const inflight = useRef(null);
  // T2.1: Gotowość otwierana z kolejki zleceń od razu na tym zleceniu.
  const [readinessOrder, setReadinessOrder] = useState(DEEP?.order || null);
  // T2.1: kreator klienta → „Nowe zlecenie” z tym klientem.
  const [orderTenant, setOrderTenant] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [dataReady, setDataReady] = useState(false); // dane konta (rola, organizacja) wczytane
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [theme, setThemeState] = useState(() => localStorage.getItem(THEME_KEY) || 'light');
  // Reset hasła: link z maila wraca tu z `type=recovery` w hashu albo `?recovery=1` — pokazujemy ekran
  // „ustaw nowe hasło" zamiast aplikacji (wcześniej link logował, ale hasła nie dało się zmienić).
  const [recovery, setRecovery] = useState(() =>
    /type=recovery/.test(window.location.hash) || /[?&]recovery=1/.test(window.location.search));

  // Theme application + persistence + system change listener
  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(THEME_KEY, theme);
    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => applyTheme('system');
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
  }, [theme]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setUser(session.user);
        setSession(session);
        fetchUserData(session.user.id, session.user);
      }
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') setRecovery(true);
      if (session) {
        setUser(session.user);
        setSession(session);
        fetchUserData(session.user.id, session.user);
      } else {
        setUser(null); setSession(null); setCredits(null); setCreditsInfo(null); setIsAdmin(false); setClientTenantId(null); setOrg(null); setDataReady(false); signupTried.current = false;
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Global Cmd+K / Ctrl+K listener
  useEffect(() => {
    if (!user) return;
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(o => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [user]);

  const fetchUserData = (userId, authUser = null) => {
    if (inflight.current?.userId === userId) return inflight.current.promise;
    const promise = loadUserData(userId, authUser).finally(() => { if (inflight.current?.promise === promise) inflight.current = null; });
    inflight.current = { userId, promise };
    return promise;
  };

  // PERF-UX (21.10): migawka konta (kredyty, rola, organizacja) w cache — po przeładowaniu aplikacja startuje OD RAZU
  // z ostatnich danych, a świeże przychodzą w tle. `Infinity` nie przechodzi przez JSON → trzymamy `unlimited`.
  const applyBoot = (snap) => {
    const cred = snap.cred ? { ...snap.cred, balance: snap.cred.unlimited ? Infinity : Number(snap.cred.balance ?? 0) } : null;
    if (cred) { setCredits(cred.balance); setCreditsInfo(cred); }
    setIsAdmin(snap.prof?.is_admin ?? false);
    setClientTenantId(snap.prof?.tenant_id ?? null);
    const row = snap.orgRow;
    setOrg(row ? { id: row.org_id, role: row.role, name: row.organizations?.name ?? 'Organizacja', credits: row.organizations?.credits ?? null, plan: row.organizations?.plan ?? null } : null);
    // T2.3: konto klienta startuje w Gotowości (nie ma „Nowego zlecenia”).
    if (snap.prof?.tenant_id && !snap.prof?.is_admin) setPage(p => (p === 'new' || p === 'live') ? 'readiness' : p);
  };

  const loadUserData = async (userId, authUser = null) => {
    setCacheScope(userId);
    const snap = getCached('boot');
    if (snap?.data) { applyBoot(snap.data); setDataReady(true); }
    const [cred, { data: prof }, { data: m }] = await Promise.all([
      fetchMyCredits(userId),
      supabase.from('profiles').select('is_admin, tenant_id').eq('id', userId).single(),
      supabase.from('org_members').select('org_id, role, organizations(id, name, credits, plan)').eq('user_id', userId).order('created_at', { ascending: true }).limit(1),
    ]);
    const row = m?.[0] ?? null;
    setCached('boot', { cred: { ...cred, balance: cred.unlimited ? null : cred.balance }, prof: prof ?? null, orgRow: row });
    applyBoot({ cred, prof, orgRow: row });
    try {
      // T2.1: agencja bez żadnego klienta (np. wróciła po rejestracji innego dnia) startuje w kreatorze, nie na pustym „Nowe zlecenie”.
      if (row && !prof?.is_admin && row.organizations?.plan !== 'mediafy') {
        const { count } = await supabase.from('tenants').select('id', { count: 'exact', head: true }).eq('org_id', row.org_id);
        if (count === 0) { setOnboarding(true); setPage('clients'); }
      }
      // v9/A3: konto z „Załóż konto” (agencja) bez organizacji → zakładamy organizację (edge org-signup, raz na sesję).
      const meta = authUser?.user_metadata || {};
      if (!row && meta.signup === 'agency' && !prof?.is_admin && !prof?.tenant_id && !signupTried.current) {
        signupTried.current = true;
        const { data, error } = await supabase.functions.invoke('org-signup', { body: { org_name: meta.org_name || '' } });
        if (error || !data?.org) {
          // supabase.functions.invoke przy 4xx daje ogólny błąd — treść z serwera jest w error.context.
          let msg = data?.error || '';
          try { if (!msg && error?.context?.json) msg = (await error.context.json())?.error || ''; } catch { /* brak treści */ }
          toast.error(`Nie udało się założyć organizacji: ${msg || error?.message || 'spróbuj ponownie'}`);
        } else {
          setOrg({ id: data.org.id, role: 'owner', name: data.org.name, credits: data.org.credits, plan: data.org.plan });
          const c = await fetchMyCredits(userId);
          setCredits(c.balance); setCreditsInfo(c);
          if (!data.existing) { setOnboarding(true); setPage('clients'); }
        }
      }
    } catch { setOrg(null); }
    setDataReady(true);
  };

  const handleLogin = (user, session) => {
    setUser(user); setSession(session);
    setPage(DEEP?.page || 'new');
    DEEP = null; // link z maila zużyty — kolejne logowanie startuje normalnie (adres i tak trzyma bieżącą stronę)
    fetchUserData(user.id, user);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    try { window.history.replaceState(null, '', window.location.pathname); } catch { /* bez history */ }
    signupTried.current = false;
    setUser(null); setSession(null);
    setCredits(null); setIsAdmin(false); setClientTenantId(null);
  };

  // 21.10 (prośba Maćka): F5 ma zostawić użytkownika w tym samym miejscu — bieżąca strona w adresie (?page=…),
  // wybrany element dopisuje sama strona (Gotowość: &order=, Klienci: &client=). Link z maila działa tak samo.
  useEffect(() => {
    if (!user || recovery) return;
    syncPageToUrl(page);
    DEEP = null;
  }, [page, user, recovery]);
  // 21.10 (uwaga Maćka): zakończone zlecenie od razu na listach + toast z przejściem do Gotowości.
  // Jeden nasłuch na całą aplikację; odpytuje tylko, gdy coś jest w toku (lib/ordersWatch.js).
  const canReadiness = isAdmin || !!clientTenantId || !!org;
  useOrdersWatch({
    enabled: !!user && dataReady && !recovery,
    userId: isAdmin && user ? user.id : null,
    onFinished: (list) => list.forEach(o => {
      const label = [o.marketplace, o.language].filter(Boolean).join(' ') || 'bez nazwy';
      if (o.status === 'done') {
        const toReadiness = canReadiness && !!o.tenant_id;
        toast.success(`Zlecenie ${label} gotowe`, {
          description: o.filename || undefined,
          duration: 15000,
          action: toReadiness
            ? { label: 'Gotowość', onClick: () => { setReadinessOrder(o.id); setPage('readiness'); } }
            : { label: 'Historia', onClick: () => setPage('history') },
        });
      } else if (o.status === 'error') {
        toast.error(`Zlecenie ${label} zakończone błędem`, { description: o.filename || undefined, duration: 15000, action: { label: 'Historia', onClick: () => setPage('history') } });
      }
    }),
  });

  // Strona niedostępna dla roli (np. konto klienta z linkiem ?page=clients) → Gotowość albo Nowe zlecenie zamiast pustego ekranu.
  useEffect(() => {
    if (!user || loading || !dataReady) return;
    const allowed = {
      clients: isAdmin || !!org, team: !!org, admin: isAdmin, myclient: !!clientTenantId,
      readiness: isAdmin || !!clientTenantId || !!org, new: !(clientTenantId && !isAdmin && !org), live: !(clientTenantId && !isAdmin && !org),
    };
    if (allowed[page] === false) setPage(clientTenantId && !isAdmin && !org ? 'readiness' : 'new');
  }, [page, user, loading, dataReady, isAdmin, org, clientTenantId]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-ink-50 dark:bg-ink-950 gap-4">
        <LogoMark size="xl" />
        <div className="flex items-center gap-2 text-sm text-ink-500 dark:text-ink-400 font-medium">
          <svg className="w-4 h-4 animate-spin text-brand-600" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.2"/>
            <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
          </svg>
          Ładowanie…
        </div>
      </div>
    );
  }

  if (user && recovery) {
    return (
      <>
        <SetPasswordPage
          email={user.email}
          onDone={() => { setRecovery(false); window.history.replaceState(null, '', window.location.pathname); }}
          onCancel={async () => { setRecovery(false); window.history.replaceState(null, '', window.location.pathname); await handleLogout(); }}
        />
        <ToastSetup />
      </>
    );
  }

  if (!user) {
    return (
      <>
        <LoginPage onLogin={handleLogin} />
        <ToastSetup />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-ink-50 dark:bg-ink-950">
      <Sidebar
        page={page}
        setPage={setPage}
        user={user}
        credits={credits}
        creditsInfo={creditsInfo}
        isAdmin={isAdmin}
        clientTenantId={clientTenantId}
        org={org}
        onLogout={handleLogout}
        onOpenPalette={() => setPaletteOpen(true)}
      />
      <main className="ml-[248px] min-h-screen">
        <div className="px-6 lg:px-10 py-8 lg:py-10 max-w-[1400px] mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={page}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            >
              {page === 'new' && !(clientTenantId && !isAdmin && !org) && <NewOrderPage  user={user} credits={credits} onCreditsChange={setCredits} onOpenReadiness={(orderId) => { setReadinessOrder(orderId); setPage('readiness'); }} initialTenantId={orderTenant} onInitialTenantUsed={() => setOrderTenant(null)} />}
              {page === 'live'     && <LiveDashboard user={user} />}
              {page === 'history'  && <HistoryPage   user={user} tenantScope={!isAdmin && (clientTenantId || org) ? { tenantId: clientTenantId, orgId: org?.id ?? null } : null} />}
              {page === 'settings' && <SettingsPage  user={user} credits={credits} theme={theme} setTheme={setThemeState} clientView={!!clientTenantId && !isAdmin && !org} />}
              {page === 'clients' && (isAdmin || org) && <ClientsPage user={user} orgId={org?.id ?? null} startWizard={onboarding} onWizardStarted={() => setOnboarding(false)} onNewOrder={(tid) => { setOrderTenant(tid); setPage('new'); }} />}
              {page === 'team' && (isAdmin || org) && org && <TeamPage org={org} />}
              {page === 'myclient' && clientTenantId && <ClientsPage user={user} fixedTenantId={clientTenantId} />}
              {page === 'readiness' && (isAdmin || clientTenantId || org) && <ReadinessPage isAdmin={isAdmin} initialOrderId={readinessOrder} onInitialOrderUsed={() => setReadinessOrder(null)} canOrderAplus={isAdmin || !!org} onCreditsChange={async () => { const c = await fetchMyCredits(user.id); setCredits(c.balance); setCreditsInfo(c); }} onConnectAccount={() => setPage((isAdmin || org) ? 'clients' : 'myclient')} />}
              {page === 'admin' && isAdmin && <AdminPage />}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        page={page}
        setPage={setPage}
        isAdmin={isAdmin}
        onLogout={handleLogout}
      />
      <ToastSetup />
    </div>
  );
}

function ToastSetup() {
  return (
    <Toaster
      position="top-right"
      richColors
      closeButton
      theme="system"
      toastOptions={{
        style: {
          fontFamily: 'Inter, system-ui, sans-serif',
          borderRadius: '10px',
          fontSize: '13px',
        },
        className: 'font-medium',
      }}
    />
  );
}
